import React, { useEffect, useState } from 'react'
import { Wrapper } from '../Dashboard/Wrapper'
import Pagination from 'react-js-pagination'
import Tippy from '@tippy.js/react'
import { BiDotsHorizontalRounded, BiShow } from 'react-icons/bi'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Footer from './../../layout/footer'
import { payoutService } from '../../_services/payout.service'
import {
    entryAmountCents,
    formatCents,
    getHostBalanceErrorMessage,
    normalizeHostBalancesResponse,
} from './hostPayoutUtils'

const SummaryCard = ({ label, amount, currency, warning = false }) => (
    <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12 tw-mb-4">
        <div className={`card tw-h-full tw-shadow-sm tw-border-l-4 ${warning ? 'tw-border-red-500' : 'tw-border-primary'}`}>
            <div className="card-body">
                <h5 className="card-title tw-text-gray-500 tw-uppercase tw-text-xs">{label}</h5>
                <h2 className={`tw-text-2xl tw-font-bold ${warning ? 'tw-text-red-700' : 'tw-text-gray-800'}`}>
                    {formatCents(amount, currency)}
                </h2>
            </div>
        </div>
    </div>
)

export const EntriesTable = ({ entries, currency }) => (
    <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="table card-table display mb-4 dataTablesCard">
            <thead>
                <tr>
                    <th>Entry</th>
                    <th>Transaction</th>
                    <th>Reservation</th>
                    <th>Amount</th>
                    <th>Updated</th>
                </tr>
            </thead>
            <tbody>
                {entries.length === 0 && (
                    <tr>
                        <td colSpan="5" className="tw-text-center tw-text-gray-500">No available wallet entries.</td>
                    </tr>
                )}
                {entries.map((entry, index) => {
                    const signedCents = entryAmountCents(entry)
                    return (
                        <tr key={entry.id || entry._id || `${entry.bookingId || 'entry'}-${index}`}>
                            <td>#{index + 1}</td>
                            <td>{entry.transactionType || '-'}</td>
                            <td>{entry.bookingId || '-'}</td>
                            <td className={signedCents < 0 ? 'tw-text-red-700' : 'tw-text-green-700'}>
                                {formatCents(signedCents, currency)}
                            </td>
                            <td>{entry.updatedAt || entry.createdAt || '-'}</td>
                        </tr>
                    )
                })}
            </tbody>
        </table>
    </div>
)

export default function HostPayoutList() {
    const [requesting, setRequesting] = useState(true)
    const [balances, setBalances] = useState(() => normalizeHostBalancesResponse(null))
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const limit = 10

    const [selectedEntries, setSelectedEntries] = useState([])
    const [selectedHost, setSelectedHost] = useState(null)
    const [showEntriesModal, setShowEntriesModal] = useState(false)

    const handleSearch = event => {
        setPage(1)
        setSearch(event.target.value)
    }

    const openEntriesModal = host => {
        setSelectedHost(host)
        setSelectedEntries(Array.isArray(host.entries) ? host.entries : [])
        setShowEntriesModal(true)
    }

    useEffect(() => {
        let active = true

        const fetchData = async () => {
            setRequesting(true)
            try {
                const response = await payoutService.getHostBalances({
                    page,
                    limit,
                    hostId: search,
                    currency: 'usd',
                })
                if (active) setBalances(normalizeHostBalancesResponse(response))
            } catch (error) {
                if (active) toast.error(getHostBalanceErrorMessage(error))
            } finally {
                if (active) setRequesting(false)
            }
        }

        fetchData()
        return () => {
            active = false
        }
    }, [page, search])

    const { hosts, summary, pagination, currency, asOf } = balances
    const hasReconciliationWarning = summary.platformPayoutBlocked
        || summary.reconciliationRequiredCents !== 0
        || summary.legacyUnverifiedPayoutCents !== 0
        || hosts.some(host => host.requiresReconciliation)
    const asOfDate = asOf && !Number.isNaN(new Date(asOf).getTime()) ? new Date(asOf).toLocaleString() : null

    return (
        <Wrapper
            showUserFilter={false}
            showDownload={false}
            handleSearch={handleSearch}
            showSearch={true}
            search={search}
            title="Host Payout Balances"
        >
            <ToastContainer toastStyle={{zIndex: 11000}} />

            {asOfDate && (
                <p className="tw-text-sm tw-text-gray-500 tw-mb-3">Balances as of {asOfDate}</p>
            )}
            <p className="tw-text-xs tw-text-gray-500 tw-mb-3">Search scope: Host ID</p>

            {hasReconciliationWarning && (
                <div className="tw-mb-4 tw-rounded-lg tw-border tw-border-red-300 tw-bg-red-50 tw-p-4 tw-text-sm tw-text-red-800" role="alert">
                    One or more host payouts require reconciliation. Do not treat those amounts as provider-paid or available for a new payout.
                </div>
            )}

            {summary.assumedCurrencyEntryCount > 0 && (
                <div className="tw-mb-4 tw-rounded-lg tw-border tw-border-amber-300 tw-bg-amber-50 tw-p-4 tw-text-sm tw-text-amber-800" role="alert">
                    {summary.assumedCurrencyEntryCount} legacy wallet {summary.assumedCurrencyEntryCount === 1 ? 'entry has' : 'entries have'} no currency and {summary.assumedCurrencyEntryCount === 1 ? 'is' : 'are'} being treated as USD.
                </div>
            )}

            <div className="row mb-2">
                <SummaryCard label="Available" amount={summary.availableCents} currency={currency} />
                <SummaryCard label="Reserved" amount={summary.reservedCents} currency={currency} />
                <SummaryCard label="In transit" amount={summary.inTransitCents} currency={currency} />
                <SummaryCard label="Provider paid" amount={summary.providerPaidCents} currency={currency} />
                <SummaryCard label="Failed and released" amount={summary.failedReleasedCents} currency={currency} warning={summary.failedReleasedCents !== 0} />
                <SummaryCard label="Reconciliation required" amount={summary.reconciliationRequiredCents} currency={currency} warning={summary.reconciliationRequiredCents !== 0} />
                <SummaryCard label="Legacy unverified payouts" amount={summary.legacyUnverifiedPayoutCents} currency={currency} warning={summary.legacyUnverifiedPayoutCents !== 0} />
            </div>

            <div className="row">
                <div className="col-xl-12">
                    <div className="tab-content">
                        <div className="tab-pane active show" id="All">
                            <div className="table-responsive">
                                {requesting && (
                                    <div className="tw-my-10 tw-flex tw-items-center tw-justify-center">
                                        <div className="bg-white tw-w-max rounded shadow-md p-4">
                                            <div className="loader"></div>
                                        </div>
                                    </div>
                                )}
                                <table className="table card-table display mb-4 dataTablesCard" id="groupTable">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Host ID</th>
                                            <th>Available entries</th>
                                            <th>Available</th>
                                            <th>Reserved</th>
                                            <th>In transit</th>
                                            <th>Provider paid</th>
                                            <th>Failed/released</th>
                                            <th>Reconciliation</th>
                                            <th>Legacy unverified</th>
                                            <th>Status</th>
                                            <th className="text-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!requesting && hosts.length === 0 && (
                                            <tr>
                                                <td colSpan="12" className="tw-text-center tw-text-gray-500">No host payout balances found.</td>
                                            </tr>
                                        )}
                                        {hosts.map((host, index) => (
                                            <tr key={host.hostId || host._id || index}>
                                                <td><span className="fs-16 font-w600">{host.name || '-'}</span></td>
                                                <td><span className="fs-16 font-w600">{host.hostId || host._id || '-'}</span></td>
                                                <td>
                                                    <span className="fs-16 badge badge-primary light">{host.availableTransactionCount || 0}</span>
                                                    {host.assumedCurrencyEntryCount > 0 && (
                                                        <span className="tw-ml-2 tw-text-xs tw-text-amber-700" title="Legacy entries treated as USD">
                                                            {host.assumedCurrencyEntryCount} assumed USD
                                                        </span>
                                                    )}
                                                </td>
                                                <td className={host.availableCents < 0 ? 'tw-text-red-700 tw-font-semibold' : 'tw-text-black tw-font-semibold'}>{formatCents(host.availableCents, currency)}</td>
                                                <td>{formatCents(host.reservedCents, currency)}</td>
                                                <td>{formatCents(host.inTransitCents, currency)}</td>
                                                <td>{formatCents(host.providerPaidCents, currency)}</td>
                                                <td className={host.failedReleasedCents ? 'tw-text-red-700' : ''}>{formatCents(host.failedReleasedCents, currency)}</td>
                                                <td className={host.reconciliationRequiredCents ? 'tw-text-red-700 tw-font-semibold' : ''}>{formatCents(host.reconciliationRequiredCents, currency)}</td>
                                                <td className={host.legacyUnverifiedPayoutCents ? 'tw-text-amber-700' : ''}>{formatCents(host.legacyUnverifiedPayoutCents, currency)}</td>
                                                <td>
                                                    {host.requiresReconciliation ? (
                                                        <span className="badge badge-danger light">Needs reconciliation</span>
                                                    ) : (
                                                        <span className="badge badge-success light">Reconciled</span>
                                                    )}
                                                </td>
                                                <td className="flex tw-text-center">
                                                    <div className="d-flex justify-content-end">
                                                        <Tippy
                                                            interactive
                                                            theme="light"
                                                            className="tw-bg-white"
                                                            arrow={false}
                                                            trigger="mouseenter"
                                                            content={
                                                                <div>
                                                                    <ul className="tw-bg-white tw-shadow-md tw-p-2 tw-m-4">
                                                                        <li className="tw-text-sm tw-text-gray-700">
                                                                            <button
                                                                                type="button"
                                                                                className="tw-block tw-p-2 tw-flex tw-items-center tw-gap-2 tw-bg-transparent tw-border-0"
                                                                                onClick={() => openEntriesModal(host)}
                                                                            >
                                                                                <BiShow /> View entries
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            }
                                                        >
                                                            <div>
                                                                <BiDotsHorizontalRounded className="tw-text-gray-500 tw-cursor-pointer" size={24} />
                                                            </div>
                                                        </Tippy>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Pagination
                activePage={pagination.currentPage || page}
                itemsCountPerPage={pagination.limit || limit}
                totalItemsCount={pagination.totalHosts}
                pageRangeDisplayed={10}
                onChange={setPage}
            />

            <Footer />
            {showEntriesModal && (
                <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-z-[5000]">
                    <div className="tw-absolute tw-inset-0 tw-bg-black/50" onClick={() => setShowEntriesModal(false)} />
                    <div className="tw-flex tw-flex-col tw-w-full tw-max-w-4xl tw-p-6 tw-z-[6000] tw-bg-white tw-rounded-lg">
                        <div className="tw-flex tw-justify-between tw-mb-4">
                            <h3 className="tw-text-xl tw-font-bold">Available entries for host {selectedHost?.hostId || selectedHost?._id || '-'}</h3>
                            <button type="button" onClick={() => setShowEntriesModal(false)} className="tw-text-gray-500 hover:tw-text-black">✕</button>
                        </div>
                        <EntriesTable entries={selectedEntries} currency={currency} />
                    </div>
                </div>
            )}
        </Wrapper>
    )
}
